import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface BuyInDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (buyIn: number) => void;
  minBuyIn: number;
  maxBuyIn: number;
  bb: number;
  joining?: boolean;
  userChips: number;
}

export const BuyInDialog = ({
  open,
  onClose,
  onConfirm,
  minBuyIn,
  maxBuyIn,
  bb,
  joining,
  userChips,
}: BuyInDialogProps) => {
  const { t } = useTranslation();
  const effectiveMax = Math.min(maxBuyIn, userChips);
  const canAfford = userChips >= minBuyIn;
  const defaultBuyIn = canAfford ? Math.min(bb * 100, effectiveMax) : minBuyIn;
  const [buyIn, setBuyIn] = useState(defaultBuyIn);

  const bbCount = Math.round(buyIn / bb);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5 text-green-400" />
            {t("lobby_buy_in_title", "Buy-in")}
          </DialogTitle>
          <DialogDescription>
            {t("lobby_buy_in_desc", "Choose your starting stack")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          <div className="text-center">
            <span className="text-3xl font-bold text-secondary">
              {buyIn.toLocaleString()}
            </span>
            <span className="text-sm text-muted-foreground ml-2">
              ({bbCount} BB)
            </span>
          </div>

          {canAfford ? (
            <>
              <Slider
                min={minBuyIn}
                max={effectiveMax}
                step={bb}
                value={[buyIn]}
                onValueChange={([v]) => setBuyIn(v)}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{minBuyIn.toLocaleString()} ({Math.round(minBuyIn / bb)} BB)</span>
                <span>{effectiveMax.toLocaleString()} ({Math.round(effectiveMax / bb)} BB)</span>
              </div>
            </>
          ) : (
            <p className="text-center text-sm text-destructive">
              {t("lobby_insufficient_chips", "Not enough chips")}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={joining}>
            {t("cancel", "Cancel")}
          </Button>
          <Button
            onClick={() => onConfirm(buyIn)}
            disabled={joining || !canAfford}
            className="bg-green-700 hover:bg-green-600"
          >
            {t("lobby_join", "Join")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
